import scraperService from './scraperService';
import categories from '../assets/categories.json';
import gamesData from '../assets/games.json';

class DataManager {
  constructor() {
    this.categories = [];
    this.games = {};  // 改为对象存储，按分类组织
    this.loadCategories();
    this.loadGames();
  }

  // 加载并处理分类数据
  loadCategories() {
    try {
      // 从 JSON 文件加载分类
      const rawCategories = categories.categories;

      // 处理分类数据，添加图标和排序
      this.categories = rawCategories
        .map(category => {
          // 为不同分类设置合适的图标
          const icon = this.getCategoryIcon(category.name);
          return {
            ...category,
            icon,
            displayName: this.formatDisplayName(category.name)
          };
        })
        // 按 order 排序
        .sort((a, b) => a.order - b.order);

    } catch (error) {
      console.error('Error loading categories:', error);
      this.categories = [];
    }
  }

  // 获取分类图标
  getCategoryIcon(categoryName) {
    const iconMap = {
      'Action': '🎯',
      'Adventure': '🗺️',
      'Arcade': '🕹️',
      'Basketball': '🏀',
      'Beauty': '💄',
      'Bike': '🏍️',
      'Car': '🚗',
      'Card': '🃏',
      'Casual': '🎲',
      'Clicker': '🖱️',
      'Controller': '🎮',
      'Dress Up': '👗',
      'Driving': '🚦',
      'Escape': '🚪',
      'Flash': '⚡',
      'FPS': '🎯',
      'Horror': '👻',
      '.io': '🌐',
      'Mahjong': '🀄',
      'Minecraft': '⛏️',
      'Pool': '🎱',
      'Puzzle': '🧩',
      'Shooting': '🎯',
      'Soccer': '⚽',
      'Sports': '🏅',
      'Stickman': '🏃',
      'Tower Defense': '🗼',
      // 默认图标
      'default': '🎮'
    };

    return iconMap[categoryName] || iconMap.default;
  }

  // 格式化显示名称
  formatDisplayName(name) {
    return name
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getCategories() {
    return this.categories;
  }

  getGames(categoryId = null) {
    if (categoryId) {
      return this.games[categoryId]?.games || [];
    }
    return this.games;
  }

  // 获取主要分类（可以根据需要过滤）
  getMainCategories() {
    return this.categories.filter(category => 
      !['Updated', 'Trending now'].includes(category.name)
    );
  }

  // 获取特定分类
  getCategoryBySlug(slug) {
    return this.categories.find(category => category.slug === slug);
  }

  // 获取热门分类
  getHotCategories() {
    return this.categories.filter(category => 
      ['Action', 'Adventure', 'Sports', 'Puzzle', 'Racing'].includes(category.name)
    ).slice(0, 5);
  }

  // 加载游戏数据
  loadGames() {
    try {
      this.games = gamesData.gamesByCategory || {};
    } catch (error) {
      console.error('Error loading games:', error);
      this.games = {};
    }
  }

  // 根据分类 slug 获取游戏
  getGamesByCategory(categorySlug) {
    try {
      // 如果分类存在，返回该分类的游戏
      if (this.games[categorySlug]) {
        return this.games[categorySlug].games.map(game => ({
          ...game,
          id: game.slug, // 确保每个游戏有唯一ID
          category: {
            name: game.categoryName,
            slug: game.categorySlug
          }
        }));
      }
      return [];
    } catch (error) {
      console.error(`Error getting games for category ${categorySlug}:`, error);
      return [];
    }
  }

  // 获取所有游戏
  getAllGames() {
    try {
      // 合并所有分类的游戏
      return Object.values(this.games)
        .reduce((allGames, category) => {
          const gamesWithCategory = category.games.map(game => ({
            ...game,
            id: game.slug,
            category: {
              name: game.categoryName,
              slug: game.categorySlug
            }
          }));
          return [...allGames, ...gamesWithCategory];
        }, []);
    } catch (error) {
      console.error('Error getting all games:', error);
      return [];
    }
  }

  // 获取游戏详情
  getGameBySlug(gameSlug) {
    try {
      // 在所有分类中查找游戏
      for (const category of Object.values(this.games)) {
        const game = category.games.find(g => g.slug === gameSlug);
        if (game) {
          return {
            ...game,
            id: game.slug,
            category: {
              name: game.categoryName,
              slug: game.categorySlug
            }
          };
        }
      }
      return null;
    } catch (error) {
      console.error(`Error getting game ${gameSlug}:`, error);
      return null;
    }
  }

  // 获取热门游戏
  getHotGames(limit = 10) {
    try {
      const trendingGames = this.games['trending-now']?.games || [];
      return trendingGames
        .slice(0, limit)
        .map(game => ({
          ...game,
          id: game.slug,
          category: {
            name: game.categoryName,
            slug: game.categorySlug
          }
        }));
    } catch (error) {
      console.error('Error getting hot games:', error);
      return [];
    }
  }
}

// 创建单例实例
const dataManager = new DataManager();
export default dataManager; 